import { useEffect, useMemo, useState } from 'react';
import { Button, ConfigProvider, DatePicker, Drawer, Input, InputNumber, Table } from 'antd';
import heIL from 'antd/locale/he_IL';
import dayjs, { type Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import { Money } from '../../components/Money';
import { ReceiptAmount } from '../../components/ReceiptAmount';
import { EmptyState } from '../../components/EmptyState';
import { useCreatePettyCashLoad, usePettyCashAvailable } from '../../hooks/usePettyCash';
import { isoToDisplay, todayIsrael } from '../../utils/dates';
import type { Receipt } from '@deposits/shared';

interface Props {
  open: boolean;
  pettyCashId: number | null;
  onClose: () => void;
  /** Called with the new load id so the parent can open its report. */
  onCreated: (movementId: number) => void;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * What a load of `amount` takes from each receipt — the same walk the server
 * does: oldest first, whole receipts, the last one possibly in part.
 */
function planLoad(receiptsOldestFirst: Receipt[], amount: number): Map<string, number> {
  const takes = new Map<string, number>();
  let left = round2(amount);
  for (const r of receiptsOldestFirst) {
    if (left <= 0) break;
    const take = round2(Math.min(r.amount, left));
    takes.set(r.external_receipt_id, take);
    left = round2(left - take);
  }
  return takes;
}

/**
 * Load an amount into the petty cash. It's taken from the oldest free cash
 * receipts; the last receipt may be taken in part and stays free with the rest.
 */
export function LoadDrawer({ open, pettyCashId, onClose, onCreated }: Props) {
  const { data, isFetching, isError } = usePettyCashAvailable(open ? pettyCashId : null);
  const create = useCreatePettyCashLoad();
  // The server returns receipts oldest-first.
  const receipts = data?.receipts ?? [];
  const available = data?.total ?? 0;

  const [amount, setAmount] = useState<number | null>(null);
  const [date, setDate] = useState<Dayjs>(() => dayjs(todayIsrael()));
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setAmount(null);
    setDate(dayjs(todayIsrael()));
    setNotes('');
  }, [open]);

  const takes = useMemo(() => planLoad(receipts, amount ?? 0), [receipts, amount]);
  const tooMuch = amount != null && round2(amount) > round2(available);
  const valid = amount != null && amount > 0 && !tooMuch;

  const submit = () => {
    if (pettyCashId == null || !valid) return;
    create.mutate(
      {
        id: pettyCashId,
        body: {
          movement_date: date.format('YYYY-MM-DD'),
          amount: round2(amount!),
          notes: notes.trim() || null,
        },
      },
      {
        onSuccess: (res) => {
          toast.success(`טעינה מספר ${res.movement_number} נרשמה`);
          if (res.pdf_status === 'failed') toast.error('הטעינה נרשמה אך נכשלה הפקת הדוח');
          onCreated(res.id);
        },
        onError: (err) => toast.error(err.message || 'שגיאה בטעינת הקופה'),
      },
    );
  };

  const columns = [
    { title: 'תאריך', dataIndex: 'issue_date', width: 110, render: (v: string) => isoToDisplay(v) },
    { title: 'מספר קבלה', dataIndex: 'receipt_number', width: 110, render: (v: string) => <span dir="ltr">{v}</span> },
    { title: 'שם לקוח', dataIndex: 'customer_name', ellipsis: true },
    {
      title: 'יתרה בקבלה',
      dataIndex: 'amount',
      width: 130,
      render: (_: number, r: Receipt) => <ReceiptAmount receipt={r} />,
    },
    {
      title: 'ייטען',
      key: 'take',
      width: 130,
      render: (_: unknown, r: Receipt) => {
        const take = takes.get(r.external_receipt_id);
        if (take == null) return null;
        const rest = round2(r.amount - take);
        return (
          <div>
            <Money amount={take} />
            {rest > 0 && (
              <div className="text-small text-secondary-text">
                יישאר <Money amount={rest} />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Drawer
      title={<span className="text-heading-3-bold text-main-text">טעינת קופה קטנה</span>}
      placement="left"
      open={open}
      onClose={onClose}
      width={640}
      closable={false}
      styles={{ header: { borderBottom: 'none' } }}
    >
      <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
        <div className="flex flex-col gap-4">
          <div className="text-small text-secondary-text">
            הסכום נלקח מקבלות המזומן הוותיקות ביותר שעוד לא הופקדו. אם הקבלה האחרונה גדולה מהנדרש,
            נלקח ממנה רק החלק החסר, והיתרה שלה נשארת פנויה לטעינה הבאה, להפקדה או לסגירת קופה.
          </div>

          <div>
            <div className="text-small text-main-text mb-1">
              סכום לטעינה <span className="text-error-red">*</span>
            </div>
            <InputNumber
              value={amount}
              onChange={(v) => setAmount(v)}
              onPressEnter={submit}
              min={0}
              precision={2}
              status={tooMuch ? 'error' : undefined}
              placeholder="0.00"
              style={{ width: 200 }}
            />
            <div className="text-small text-secondary-text mt-1">
              מזומן פנוי: <Money amount={available} />
              {tooMuch && <span className="text-error-red"> — הסכום גבוה מהמזומן הפנוי</span>}
            </div>
          </div>

          <Table<Receipt>
            rowKey="external_receipt_id"
            columns={columns as any}
            dataSource={receipts}
            loading={isFetching ? { spinning: true } : false}
            size="small"
            rowClassName={(r) => (takes.has(r.external_receipt_id) ? 'bg-blue-bg' : '')}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, size: 'small' }}
            scroll={{ y: 'calc(100vh - 520px)' }}
            locale={{
              emptyText: isError ? (
                <EmptyState title="שגיאה בטעינת הקבלות" description="נסה שוב" />
              ) : (
                <EmptyState title="אין קבלות מזומן פנויות" description="כל קבלות המזומן כבר הופקדו או נטענו" />
              ),
            }}
          />

          <div className="border border-border rounded p-3 bg-blue-bg flex items-center justify-between">
            <span>ייטען מ-{takes.size} קבלות</span>
            <span className="font-semibold text-heading-3">
              <Money amount={valid ? round2(amount!) : 0} />
            </span>
          </div>

          <div>
            <div className="text-small text-main-text mb-1">
              תאריך <span className="text-error-red">*</span>
            </div>
            <DatePicker
              value={date}
              onChange={(d) => d && setDate(d)}
              format="DD/MM/YYYY"
              allowClear={false}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <div className="text-small text-main-text mb-1">הערות</div>
            <Input.TextArea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} rows={2} />
          </div>

          <div className="flex justify-end gap-3">
            <Button onClick={onClose}>ביטול</Button>
            <Button
              type="primary"
              onClick={submit}
              loading={create.isPending}
              disabled={!valid}
            >
              טען לקופה
            </Button>
          </div>
        </div>
      </ConfigProvider>
    </Drawer>
  );
}
